import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import "./ResultPage.css"; // Reuse the same CSS
import "./PracticeResultPage.css";
import ViewAnswers from "./ViewAnswers";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar
} from 'recharts';


function PracticeResultPage({ }) {
  const navigate = useNavigate();

  const location = useLocation();
  const resultData = location.state || {};

  const {
    questions,
    selectedAnswers,
    questionTimes,
    examType
  } = resultData;

   console.log("🧩 PracticeResultPage Data:", {
  questions,
  questionTimes,
  selectedAnswers,
  examType,
});

  const totalQuestionsCount = questions?.length || 0;

  // Correct and wrong counts (fallback if not provided)
  const correctCount = questions?.reduce((acc, q, idx) => {
    const sel = selectedAnswers?.[idx];
    if (!sel) return acc;

    if (q.type === 'single') return sel === q.correct_option ? acc + 1 : acc;

    const correctArr = Array.isArray(q.correct_option_array)
      ? q.correct_option_array
      : [q.correct_option];
    const selArr = Array.isArray(sel) ? sel : [];
    const isCorrect =
      correctArr.length === selArr.length &&
      correctArr.every(v => selArr.includes(v));
    return isCorrect ? acc + 1 : acc;
  }, 0);

  const wrongCount = totalQuestionsCount - correctCount;

  // Practice stats (average, fastest, slowest)
  const practiceStats = React.useMemo(() => {
    if (!questionTimes || !questions) return null;

    const entries = Object.entries(questionTimes); // [ [idx, timeSec], ... ]
    if (!entries.length) return null;

    const times = entries.map(([_, t]) => t);
    const totalSec = times.reduce((a, b) => a + b, 0);
    const avgSec = totalSec / times.length;
    const maxTime = Math.max(...times);
    const minTime = Math.min(...times);

    const slowestQ = entries.find(([_, t]) => t === maxTime)[0];
    const fastestQ = entries.find(([_, t]) => t === minTime)[0];

    const avgReadable = `${Math.floor(avgSec)} sec`;

    const feedback =
      avgSec > 120
        ? "⏱ You're taking longer per question. Try improving your speed."
        : avgSec > 60
          ? "💡 Good pace, but can be faster."
          : "🔥 Excellent speed!";

    // Performance by question type
    const performanceByType = {};
    questions.forEach((q, idx) => {
      const sel = selectedAnswers?.[idx];
      const time = questionTimes[idx] || 0;
      const correct = q.type === 'single'
        ? sel === q.correct_option
        : JSON.stringify(sel?.sort()) === JSON.stringify(q.correct_option_array?.sort());

      if (!performanceByType[q.type]) performanceByType[q.type] = { total: 0, correct: 0, totalTime: 0 };
      performanceByType[q.type].total += 1;
      performanceByType[q.type].correct += correct ? 1 : 0;
      performanceByType[q.type].totalTime += time;
    });

    Object.keys(performanceByType).forEach(type => {
      const d = performanceByType[type];
      d.avgTime = Math.floor(d.totalTime / d.total);
      d.accuracy = Math.round((d.correct / d.total) * 100);
    });

    const suggestions = Object.entries(performanceByType).map(([type, stats]) => {
      if (stats.accuracy < 50) return `🎯 Focus on ${type} questions — accuracy is low (${stats.accuracy}%)`;
      if (stats.avgTime > 60) return `⚡ Improve speed on ${type} questions — avg ${stats.avgTime}s`;
      return `✅ ${type} questions are good — keep practicing!`;
    });

    return {
      avgSec,
      maxTime,
      minTime,
      slowestQ: parseInt(slowestQ) + 1,
      fastestQ: parseInt(fastestQ) + 1,
      avgReadable,
      feedback,
      performanceByType,
      suggestions,
      chartData: entries.map(([idx, t]) => ({ question: `Q${parseInt(idx) + 1}`, time: t })),
    };
  }, [questions, selectedAnswers, questionTimes]);


  const [showAnswers, setShowAnswers] = useState(false);


  // Example fallback data if resultData is not provided
  const {
    totalQuestions,
    attempted,
    unattempted,
    correct,
    wrong,
    score,
    totalDuration,
    timeTaken,
  } = resultData || {};

  // After practiceStats is calculated
  const avgTime = practiceStats?.avgSec || 0;

  // Add this function here
  const getQuestionPerformance = (time) => {
    if (time < avgTime * 0.8) return { color: '#4caf50', emoji: '⚡' }; // faster than average → Green
    if (time <= avgTime * 1.2) return { color: '#ffa500', emoji: '⭐' }; // around average → Orange
    return { color: '#f44336', emoji: '🐢' }; // slower than average → Red
  };


  return (
    
    <div className="result-container">
      <h2 className="result-heading">📈 Practice Result 📈</h2>
      <p className="result-subtext">Your performance summary</p>

      <div className="summary-grid">
  <div className="summary-item total">
    <div className="summary-icon">🧮</div>
    <div className="summary-title">Total Questions</div>
    <div className="summary-value">{totalQuestions}</div>
  </div>
  <div className="summary-item attempted">
    <div className="summary-icon">✍️</div>
    <div className="summary-title">Attempted</div>
    <div className="summary-value">{attempted}</div>
  </div>
  <div className="summary-item unattempted">
    <div className="summary-icon">🚫</div>
    <div className="summary-title">Unattempted</div>
    <div className="summary-value">{unattempted}</div>
  </div>
  <div className="summary-item correct">
    <div className="summary-icon">✅</div>
    <div className="summary-title">Correct</div>
    <div className="summary-value">{correct}</div>
  </div>
  <div className="summary-item wrong">
    <div className="summary-icon">❌</div>
    <div className="summary-title">Wrong</div>
    <div className="summary-value">{wrong}</div>
  </div>
  <div className="summary-item score">
    <div className="summary-icon">🏆</div>
    <div className="summary-title">Score (%)</div>
    <div className="summary-value">{score}%</div>
  </div>
</div>


      <div className="result-time-section">
  <h3 className="result-time-heading">⏰ Time Analysis</h3>
  <div className="time-grid">
    <div className="time-row">
      <div className="time-left">
        <span className="time-icon">🟢</span> Started At
      </div>
      <div className="time-right badge">
        {resultData.started_at
          ? new Date(resultData.started_at).toLocaleString()
          : "N/A"}
      </div>
    </div>

    <div className="time-row">
      <div className="time-left">
        <span className="time-icon">🔴</span> Submitted At
      </div>
      <div className="time-right badge">
        {resultData.submitted_at
          ? new Date(resultData.submitted_at).toLocaleString()
          : "N/A"}
      </div>
    </div>

    <div className="time-row">
      <div className="time-left">
        <span className="time-icon">⌛</span> Time Taken
      </div>
      <div className="time-right badge">{resultData.timeTaken}</div>
    </div>
  </div>
</div>



      {practiceStats && (
        <div className="practice-performance">
          <h3 className="practice-heading">📊 Practice Performance Analysis</h3>

          <div className="practice-grid">
            <div className="practice-item highlight">
              <span className="practice-icon">⏱️</span>
              <span className="practice-label">Average Time / Question</span>
              <div className="practice-value">{practiceStats.avgReadable}</div>
            </div>

            <div className="practice-item fastest">
              <span className="practice-icon">⚡</span>
              <span className="practice-label">Fastest Question</span>
              <div className="practice-value">Q{practiceStats.fastestQ} ({practiceStats.minTime}s)</div>
            </div>

            <div className="practice-item slowest">
              <span className="practice-icon">🐢</span>
              <span className="practice-label">Slowest Question</span>
              <div className="practice-value">Q{practiceStats.slowestQ} ({practiceStats.maxTime}s)</div>
            </div>
          </div>


          <div className="feedback-section">
            <p><strong>🧠 Feedback:</strong> {practiceStats.feedback}</p>
            <p><strong>🎯 Suggestions:</strong></p>
            <ul>
              {practiceStats.suggestions.map((s, idx) => <li key={idx}>{s}</li>)}
            </ul>
          </div>

          {/* NEW: Per Question Time List */}
          {questions && questionTimes && Object.keys(questionTimes).length > 0 && (
            <div className="per-question-time">
              <h4>⏱ Time Spent Per Question</h4>
              <ul>
                {questions.map((q, idx) => {
                  const time = questionTimes[idx] ?? 0;
                  const { color, emoji } = getQuestionPerformance(time);
                  return (
                    <li
                      key={idx}
                      className="question-time-card"
                      style={{ borderLeft: `6px solid ${color}` }} // colored border
                    >
                      <div className="question-time-info">
                        <span className="question-number">Q{idx + 1} {emoji}</span>
                        <span className="question-time">{time} sec</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}


          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={practiceStats.chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="question" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="time" barSize={30} fill="#4a90e2" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="result-buttons">
        <button className="view-answers-btn" onClick={() => setShowAnswers(!showAnswers)}>
          {showAnswers ? "Hide Answers" : "View Answers"}
        </button>

        <button
          className="try-again-btn"
          onClick={() => {
            const finalExamId = resultData.examId || resultData?.questions?.[0]?.exam_id;
            if (!finalExamId) {
              alert("Cannot retry: Exam ID missing");
              return;
            }

            navigate('/practice', {
              state: {
                examId: finalExamId,
                topicId: resultData.topicId,
                subjectId: resultData.subjectId,
                questions: resultData.questions
              }
            });
          }}
        >
          Try Again
        </button>

         <button
  className='dashboard-btn'
  onClick={() => navigate('/dashboard', { state: { fromResultPage: true } })}
>
  Go to Dashboard
</button>

      </div>

      {showAnswers && (
        <ViewAnswers
          questions={resultData.questions}
          selectedAnswers={resultData.selectedAnswers}
        />
      )}
    </div>
  );
}

export default PracticeResultPage;
